import { useCallback, useEffect, useState } from "react";
import { ArrowUpRight, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { BRAND, REPORT, AUDIO, JOURNEY } from "@/config/content";
import Interlude, { seen } from "./Interlude";
import ProfileBars from "./ProfileBars";

// Divide parágrafos longos em blocos menores para leitura no celular.
export const splitLong = (text, max = 480) => {
  if (text.length <= max) return [text];
  const out = [];
  let cur = "";
  text.split(/(?<=[.!?])\s+/).forEach((s) => {
    if (cur && (cur + " " + s).length > max * 0.7) {
      out.push(cur);
      cur = s;
    } else cur = cur ? `${cur} ${s}` : s;
  });
  if (cur) out.push(cur);
  return out;
};

const Offer = ({ offer }) => (
  <section className="mt-16 rounded-3xl bg-[#1c1917] p-7 text-stone-200 sm:p-10" data-testid="report-offer">
    <h2 className="text-2xl text-white sm:text-3xl" data-testid="offer-title">{REPORT.offerTitle}</h2>
    {offer ? (
      <>
        <div className="mt-5 space-y-3 leading-relaxed text-stone-300" data-testid="offer-intro">
          {REPORT.offerIntro.map((p) => <p key={p}>{p}</p>)}
        </div>
        <h3 className="mt-8 text-xl text-white" data-testid="offer-name">{offer.name}</h3>
        <p className="mt-2 text-sm leading-relaxed text-stone-400" data-testid="offer-description">{offer.description}</p>
        <a href={offer.url} target={offer.kind === "whatsapp" ? "_blank" : undefined} rel="noopener noreferrer" className="btn-accent mt-8 w-full sm:w-auto" data-testid="offer-checkout-button">
          {REPORT.offerCta} <ArrowUpRight size={18} />
        </a>
      </>
    ) : (
      <p className="mt-5 leading-relaxed" data-testid="offer-unavailable">{REPORT.offerUnavailable}</p>
    )}
  </section>
);

const Pending = ({ data, onRetry }) => (
  <div className="rounded-3xl border bg-white p-8 text-center" data-testid="report-pending">
    {data.status === "failed" ? (
      <>
        <p className="text-stone-700">Suas respostas estão salvas, mas não conseguimos escrever seu relatório agora.</p>
        {data.can_retry
          ? <button className="btn-primary mt-6" onClick={onRetry} data-testid="report-retry-button">Tentar novamente</button>
          : <p className="mt-4 text-sm text-stone-500">Entre em contato com o suporte para concluirmos seu relatório.</p>}
      </>
    ) : (
      <p className="flex items-center justify-center gap-3 text-stone-700"><Loader2 className="animate-spin" size={18} /> Estamos escrevendo sua devolutiva. Isso leva cerca de um minuto…</p>
    )}
  </div>
);

export default function ReportView() {
  const [data, setData] = useState(null);
  const [opened, setOpened] = useState(() => seen.has("closing"));
  const load = useCallback(() => api.get("/report").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), []);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (data?.status !== "generating") return undefined;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [data, load]);

  const retry = async () => {
    try {
      await api.post("/report/retry");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get("/report/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "meu-mapa-da-virada-360.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Não foi possível gerar o PDF agora. Tente novamente.");
    }
  };

  if (!data && opened) return <div className="p-16 text-center text-stone-500">Carregando…</div>;
  const r = data?.report;
  if (!opened) {
    return (
      <Interlude id="closing" {...JOURNEY.closing} cta={null} audio={AUDIO.delivery}>
        <div className="mt-8" data-testid="closing-status">
          {r ? (
            <button type="button" className="btn-primary w-full sm:w-auto" onClick={() => { seen.add("closing"); setOpened(true); window.scrollTo({ top: 0 }); }} data-testid="closing-open-report-button">
              {JOURNEY.closing.cta} <ArrowUpRight size={18} />
            </button>
          ) : data?.status === "failed" ? <Pending data={data} onRetry={retry} /> : (
            <p className="flex items-center gap-3 text-stone-700" data-testid="closing-preparing"><Loader2 className="animate-spin" size={18} /> {JOURNEY.closing.preparing}</p>
          )}
        </div>
      </Interlude>
    );
  }
  return (
    <article className="mx-auto max-w-2xl px-5 pb-20 pt-4 fade-up" data-testid="report-view">
      <span className="eyebrow">{BRAND.school}</span>
      <h1 className="mt-4 text-4xl text-stone-900 sm:text-5xl" data-testid="report-title">{BRAND.product}</h1>
      <p className="mt-3 text-stone-600" data-testid="report-owner">{data.nome} · {data.negocio}</p>
      <div className="mt-10"><ProfileBars scores={data.scores} /></div>
      <div className="mt-12">
        {!r ? <Pending data={data} onRetry={retry} /> : (
          <>
            <div className="prose-narrative" data-testid="report-narrative">{r.narrativa.flatMap((p) => splitLong(p)).map((p, i) => <p key={i}>{p}</p>)}</div>
            <div className="my-10 space-y-5 border-l-2 border-[#b85d3b] pl-6" data-testid="report-questions">
              {r.perguntas_provocativas.map((q, i) => <p key={i} className="font-display text-xl italic leading-snug text-stone-900">{q}</p>)}
            </div>
            {r.reflexao_final && <p className="text-lg leading-relaxed text-stone-800" data-testid="report-reflection">{r.reflexao_final}</p>}
            <section className="mt-14" data-testid="report-actions">
              <h2 className="text-2xl text-stone-900 sm:text-3xl">{REPORT.actionsTitle}</h2>
              <ol className="mt-6 space-y-4">
                {r.acoes.map((a, i) => (
                  <li key={i} className="rounded-2xl border bg-white p-5" data-testid={`report-action-${i}`}>
                    <span className="font-display text-[#b85d3b]">{i + 1}</span>
                    <h3 className="mt-1 text-lg text-stone-900">{a.titulo}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-stone-600">{a.descricao}</p>
                  </li>
                ))}
              </ol>
            </section>
            <Offer offer={data.offer} />
            <button type="button" onClick={downloadPdf} className="btn-primary mt-8 w-full sm:w-auto" data-testid="report-download-pdf-button">
              <Download size={18} /> Baixar meu Mapa em PDF
            </button>
          </>
        )}
      </div>
      <p className="mt-12 text-xs leading-relaxed text-stone-500">Este Mapa é uma ferramenta de reflexão baseada nas suas respostas. Não é consultoria completa, avaliação psicológica, diagnóstico científico de personalidade nem garantia de resultados.</p>
    </article>
  );
}
